import { create } from "zustand";
import type { Party } from "@/types/party";
import type { Deadline } from "@/types/deadline";
import type { TimelineEvent } from "@/types/timeline";
import type { DocumentListItem } from "@/types/document";

import type { ViewerPermissions } from "@/types/gateway";

export interface CaseWorkspaceData {
  /**
   * The case's real title. **OPTIONAL and never fabricated.** The hook used to
   * substitute `Case ${caseId}` when the runtime had none, which put a raw id
   * into user-facing copy — the exact leak B2 closed for the breadcrumbs, and
   * it fed `useScopeNames`' FIRST resolution step, so it would have won over
   * every honest source below it.
   *
   * `undefined` ⇒ this case has no title. Render a generic label
   * (`useCaseLabel(matterType)`), never the id.
   */
  title?: string;
  status: string;
  /**
   * D5a: cascade-resolved role. Absent when the runtime could not resolve
   * a grant (either userId+orgId were omitted from case.open, or the
   * resolver found no matching assignment). NEVER fabricate "OBSERVER" —
   * render "—" / "Role unresolved" and disable role-gated affordances.
   */
  role?: string;
  /**
   * ⚖ D-64: what THE VIEWER may do — mirrored from `case.open` verbatim, never
   * derived. Read it ONLY through `lib/case-permissions.ts`; the object's
   * PRESENCE carries half the meaning, so `data.viewerPermissions?.can_write`
   * at a call site destroys the distinction the selector exists to keep.
   *
   * ⚠ Distinct SUBJECT from `role` above: `role` resolves from `params.userId`
   * (client input — LexPortal asks "what is USER X's role"), while this
   * resolves from the RUNTIME PRINCIPAL. They can be truthful answers about
   * DIFFERENT PEOPLE. Affordances key off THIS one.
   *
   * ⚠ Affordances only — never enforcement (§6.6.3 is the gate).
   */
  viewerPermissions?: ViewerPermissions;
  /** D5a: denormalized matterType from the parent Matter. Drives the matterType-aware tab registry. */
  matterType?: string;
  /**
   * Capability tokens advertised by the runtime on `case.open` (e.g.
   * `["case.refresh"]`). ABSENT ⇒ nothing advertised ⇒ the dependent
   * affordance is not rendered. Never defaulted to `[]` here: an empty array
   * and "the runtime never said" are the same DECISION (hide) but not the same
   * FACT, and inventing `[]` would hide that the field never arrived.
   */
  capabilities?: string[];
  /**
   * The `m-case-updated` brief + its metadata, from `case.context` (§3.4-ZERO).
   * `brief` present-but-EMPTY is a correct state (nothing to summarise), which
   * is why the raw values are stored rather than a pre-collapsed boolean —
   * absent, empty and present are three different sentences to the lawyer.
   */
  brief?: import("@/types/gateway").CaseBrief;
  briefMeta?: import("@/types/gateway").CaseBriefMeta;
  /** CLOSED vocabulary of field names whose value is not the whole truth. */
  incomplete?: string[];
  /**
   * §4.P.X.topology: caller's orgId, decoded from the Lexiun JWT's
   * `org` claim and written at workspace-load time. Populated by
   * `useCaseWorkspace`. Required for the harness POST body (every
   * scope needs orgId, per consumer-guide.md §7); also keeps reads
   * synchronous so the HarnessesSidePanel doesn't need its own
   * `getCurrentOrgId()` effect.
   */
  orgId: string;
  /** D5a: parent Client id, populated from case.open. OPTIONAL (D-8, flat storage):
   *  `undefined` for an assignment-pending (unassigned) case opened by caseId alone
   *  (matches the SPA's optional-parent convention — `undefined`, not `null`, so the
   *  harness/session-topology consumers that take `string | undefined` need no coercion). */
  clientId: string | undefined;
  /** D5a: parent Matter id, populated from case.open. OPTIONAL (D-8). */
  matterId: string | undefined;
  parties: Party[];
  documents: DocumentListItem[];
  deadlines: Deadline[];
  timeline: TimelineEvent[];
  /**
   * Which of the four fan-out signals could NOT be read this load
   * (breadcrumb redesign §3.4.3-bis — the precondition for the B4b attention
   * strip).
   *
   * ## Why an array and not just an empty list
   *
   * These four RPCs used to be wrapped in `.catch(() => [])`, so `deadlines: []`
   * meant either **"this case has no deadlines"** or **"the deadline call
   * failed"** — and nothing could tell them apart. That was harmless while the
   * widgets rendered an empty list either way. It stops being harmless the
   * moment anything ASSERTS a count: a failed `deadline.list` renders no
   * deadline warning, which the lawyer reads as *"nothing is due"*. A confident
   * zero we cannot prove is worse than saying nothing.
   *
   * So a failed list still arrives as `[]` (every existing consumer keeps
   * working, unchanged) but its name appears here. **Anything that draws a
   * conclusion from a count MUST check this first and stay silent about a
   * signal it could not read.** Rendering "0 prazos" from an unreadable
   * `deadlines` is the defect this field exists to make impossible.
   */
  unreadable: Array<"parties" | "documents" | "deadlines" | "timeline">;
}

interface CaseWorkspaceStoreState {
  /** Currently open case ID */
  caseId: string | null;
  /** Workspace data (null until loaded) */
  data: CaseWorkspaceData | null;
  isLoading: boolean;
  error: string | null;

  setWorkspace: (caseId: string, data: CaseWorkspaceData) => void;
  setLoading: (caseId: string, loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Phase 5.7.c — swap the `documents` array without touching the
   *  other (heavier) workspace fields. Consumed by
   *  `useCaseWorkspace`'s `document.updated` refresh listener. */
  setDocuments: (caseId: string, documents: DocumentListItem[]) => void;
  clear: () => void;
}

export const useCaseWorkspaceStore = create<CaseWorkspaceStoreState>((set) => ({
  caseId: null,
  data: null,
  isLoading: false,
  error: null,

  setWorkspace: (caseId, data) => set({ caseId, data, isLoading: false, error: null }),
  setLoading: (caseId, loading) => set({ caseId, isLoading: loading, ...(loading ? { error: null, data: null } : {}) }),
  setError: (error) => set({ error, isLoading: false }),
  setDocuments: (caseId, documents) =>
    set((state) => {
      // Guard against a stale refresh landing after the user switched
      // cases — the async RPC in `useCaseWorkspace` could resolve
      // against the previous case just as the new one is loading.
      if (state.caseId !== caseId || !state.data) return state;
      // A successful refresh CLEARS `documents` from `unreadable` — otherwise a
      // list that failed at load time would stay marked unreadable forever even
      // though we are now holding a good result, and every count derived from it
      // would go on being suppressed. The flag must describe the data currently
      // in the store, not the worst thing that ever happened to it.
      return {
        data: {
          ...state.data,
          documents,
          unreadable: state.data.unreadable.filter((k) => k !== "documents"),
        },
      };
    }),
  clear: () => set({ caseId: null, data: null, isLoading: false, error: null }),
}));

// DEV-only: Playwright specs patch `data.role` to cover the 5.4.b
// role-gate + OBSERVER branches without needing a lexserver role
// seed. Stripped in production by Vite DCE when DEV is false.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__cwsStore", {
    value: useCaseWorkspaceStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
